import { Input } from '@common/components/atoms/Input';
import { Icon } from '@common/components/foundations/icons';
import { Typography } from '@common/components/foundations/Typography';
import { Modal } from '@common/components/molecules/Modal';
import { useAuthStore } from '@common/store/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdatePartner } from '@pages/Admin/PartnerUsers/hooks/useUpdatePartner';
import { SelectedPartner } from '@pages/Admin/PartnerUsers/types/selectedPartner';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onClick?: () => void;
  children?: React.ReactNode;
  selected?: SelectedPartner;
};

const schema = z.object({
  token: z.string().or(z.undefined()).nullable(),
  email: z.string().email(),
  name: z.string(),
  id: z.number()
});

export const UpdatePartnerModal = ({ isOpen, onClose, selected }: ModalProps) => {
  const { token } = useAuthStore();
  const { updatePartner } = useUpdatePartner();

  const { control, reset, handleSubmit, formState } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      token,
      ...selected
    }
  });

  const onSubmit = (data: any) => {
    if (selected?.id) {
      const payloadData = {
        payload: {
          token,
          ...data
        },
        id: selected?.id
      };
      updatePartner(payloadData);
    }
  };

  useEffect(() => {
    if (selected) {
      reset(selected);
    }
  }, [selected]);

  return (
    <Modal
      okText="Save"
      continueIntent="primary"
      size="lg"
      open={isOpen}
      disableCloseOnOk={!formState.isValid}
      onClick={handleSubmit(onSubmit)}
      onClose={() => {
        onClose();
      }}
      children={
        <div>
          <Typography className="mb-8" element="h6" color="darkGrey" variant="h6">
            Edit Partner: {selected?.name}
          </Typography>
          <div className="flex w-full flex-col justify-between gap-4 ">
            <Controller
              name="name"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  name="subject"
                  background="grey"
                  type="text"
                  value={value}
                  label="Name *"
                  placeholder="Please enter name"
                  onChange={onChange}
                  onClear={() => reset({ name: '' })}
                  error={!!error}
                  helperMessage={
                    error && (
                      <div className="flex items-center gap-2 text-error">
                        {error ? <Icon.WarningOutline className="w-4" /> : ''}
                        <Typography element="span" color="error" variant="meta-2">
                          {error ? error.message?.toString() : ''}
                        </Typography>
                      </div>
                    )
                  }
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  name="subject"
                  background="grey"
                  type="email"
                  value={value}
                  label="Email *"
                  placeholder="Please enter email"
                  onChange={onChange}
                  onClear={() => reset({ email: '' })}
                  error={!!error}
                  helperMessage={
                    error && (
                      <div className="flex items-center gap-2 text-error">
                        {error ? <Icon.WarningOutline className="w-4" /> : ''}
                        <Typography element="span" color="error" variant="meta-2">
                          {error ? error.message?.toString() : ''}
                        </Typography>
                      </div>
                    )
                  }
                />
              )}
            />
          </div>
        </div>
      }
    />
  );
};
